/**
 * Sign In Button Component
 * Handles user authentication state with MSAL
 * Shows sign-in button when unauthenticated, user persona with sign-out when authenticated
 * Supports Teams context with visual indicator
 */

import { useMsal } from '@azure/msal-react'
import {
  Button,
  Persona,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { SignOutRegular } from '@fluentui/react-icons'
import { useAuthContext } from '../auth/AuthProvider'
import { getLoginRequest } from '../auth/authConfig'

const useStyles = makeStyles({
  signInButton: {
    minWidth: '100px',
  },
  personaContainer: {
    cursor: 'pointer',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationNormal,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  personaWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  menuItem: {
    color: tokens.colorNeutralForeground1,
  },
  teamsBadge: {
    backgroundColor: tokens.colorBrandBackground,
  },
})

export function SignInButton() {
  const styles = useStyles()
  const { instance, accounts } = useMsal()
  const { isTeams, isAuthenticated } = useAuthContext()

  const handleSignIn = async () => {
    try {
      // Always use popup for Teams compatibility
      await instance.loginPopup(getLoginRequest())
    } catch (error) {
      // User cancelled or error occurred
      console.error('Sign in failed:', error)
    }
  }

  const handleSignOut = async () => {
    try {
      // Always use popup for Teams compatibility
      await instance.logoutPopup()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  if (!isAuthenticated) {
    return (
      <Button
        appearance="primary"
        className={styles.signInButton}
        onClick={handleSignIn}
      >
        Sign In
      </Button>
    )
  }

  const account = accounts[0]
  const displayName = account?.name || account?.username || 'User'
  const email = account?.username || ''

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <div className={styles.personaContainer}>
          <div className={styles.personaWrapper}>
            <Persona
              name={displayName}
              secondaryText={email}
              presence={{ status: 'available' }}
              avatar={{ color: 'brand' }}
            />
            {isTeams && (
              <Badge
                appearance="filled"
                size="small"
                className={styles.teamsBadge}
              >
                Teams
              </Badge>
            )}
          </div>
        </div>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={<SignOutRegular />}
            onClick={handleSignOut}
            className={styles.menuItem}
          >
            Sign Out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  )
}
